import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  EmsApiClient,
  grantIsWithin,
  type AccessGrant,
  type AnalysisAvailability,
  type AnalysisRequest,
  type AnalysisResponse,
  type Brand,
  type Company,
  type CpePerformancePeriod,
  type CpePerformancePeriodInput,
  type DeliveryPoint,
  type DemoDatabase,
  type Equipment,
  type LegalEntity,
  type Meter,
  type Role,
  type Session,
  type Site,
  type SiteArea,
  type SiteTagAssignment,
  type SiteWeatherCoverage,
  type SiteOfficialDegreeDays,
  type TagDefinition,
  type User,
  type WorkflowItem,
} from "@syllarys/contracts";
import { authenticatedApiFetch } from "../services/apiTransport";

export const apiClient = new EmsApiClient("/api/v1", authenticatedApiFetch);

export const SESSION_QUERY_KEY = ["session"] as const;
export const BRAND_DATA_QUERY_KEY = "brand-data";
export const MEASUREMENTS_QUERY_KEY = "measurements";
export const POINT_SYNCS_QUERY_KEY = "point-syncs";
const SIGNED_IN_MARKER = "syllarys_ems_session_signed_in=";
const PREVIEW_ROLE_KEY = "syllarys-ems-ui:v1:preview-role";

/** Rôle que le super-admin peut choisir de voir « comme si » ; aperçu seulement. */
export type PreviewRole = "manager";

function storedPreviewRole(): PreviewRole | null {
  try {
    return window.sessionStorage.getItem(PREVIEW_ROLE_KEY) === "manager"
      ? "manager"
      : null;
  } catch {
    return null;
  }
}

function storePreviewRole(role: PreviewRole | null): void {
  try {
    if (role) window.sessionStorage.setItem(PREVIEW_ROLE_KEY, role);
    else window.sessionStorage.removeItem(PREVIEW_ROLE_KEY);
  } catch {
    // L'aperçu reste utilisable dans l'onglet sans stockage.
  }
}

/**
 * L'API pose un marqueur non secret à côté du cookie de session HttpOnly.
 * Sans ce marqueur, aucune session n'est sondée : un visiteur anonyme ne
 * déclenche pas de 401.
 */
function hasSignedInMarker(): boolean {
  try {
    return document.cookie
      .split(";")
      .some((entry) => entry.trim().startsWith(SIGNED_IN_MARKER));
  } catch {
    return true;
  }
}

/**
 * Données de la marque active au format partagé `DemoDatabase`, assemblées
 * depuis les routes unitaires de l'API. Les séries ne portent que leurs
 * métadonnées : les valeurs sont chargées par `useSeriesValues`.
 */
export type BrandData = DemoDatabase;

interface LoginResult {
  requiresTotp: boolean;
  challengeId?: string;
  role?: Role;
}

type WorkflowKind = "anomalies" | "recommendations" | "works" | "reports";

interface EmsContextValue {
  ready: boolean;
  apiState: "loading" | "ready" | "error" | "forbidden";
  apiError: string;
  retryApi: () => Promise<void>;
  db: BrandData;
  session: Session | null;
  /**
   * Rôle qui gouverne l'interface : celui de la session, ou le rôle d'aperçu
   * choisi par un super-admin (`previewRole`). L'identité et les droits
   * envoyés à l'API restent ceux de la session.
   */
  role: Role | null;
  /** Aperçu en cours pour un super-admin ; `null` sinon. */
  previewRole: PreviewRole | null;
  /** Active ou quitte l'aperçu ; sans effet pour un autre profil qu'un super-admin. */
  setPreviewRole: (role: PreviewRole | null) => void;
  login: (email: string, password: string) => Promise<LoginResult>;
  confirmTotp: (challengeId: string, code: string) => Promise<Role>;
  logout: () => Promise<void>;
  switchBrand: (brandId: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshBrandData: () => Promise<void>;
  resetDemo: () => Promise<void>;
  upsertUser: (user: User, initialGrant?: AccessGrant) => Promise<void>;
  setUserStatus: (
    userId: string,
    status: "active" | "suspended" | "disabled",
  ) => Promise<void>;
  removeUser: (userId: string, confirmationEmail: string) => Promise<void>;
  updateUserGrants: (
    userId: string,
    membershipId: string,
    grants: AccessGrant[],
  ) => Promise<void>;
  upsertBrand: (brand: Brand, legalEntity?: LegalEntity) => Promise<void>;
  upsertCompany: (company: Company) => Promise<void>;
  upsertSite: (site: Site) => Promise<void>;
  /**
   * Renvoie `true` quand l'API synchronise le point en arrière-plan (PRM ou
   * PCE actif créé, ou dont la référence change) : le point figure alors dans
   * `syncingPointIds` et les données de la marque sont rechargées dès que
   * la synchronisation se termine.
   */
  upsertDeliveryPoint: (point: DeliveryPoint) => Promise<boolean>;
  /** Points dont une synchronisation ciblée tourne encore côté API. */
  syncingPointIds: string[];
  upsertSiteArea: (area: SiteArea) => Promise<void>;
  removeSiteArea: (areaId: string) => Promise<void>;
  upsertTag: (tag: TagDefinition) => Promise<void>;
  toggleSiteTag: (assignment: SiteTagAssignment) => Promise<void>;
  upsertMeter: (meter: Meter) => Promise<void>;
  removeMeter: (meterId: string) => Promise<void>;
  /** Période de performance (CPE) d'un Site ; l'énergie observée est calculée si absente. */
  upsertPerformancePeriod: (
    periodId: string,
    input: CpePerformancePeriodInput,
  ) => Promise<CpePerformancePeriod>;
  removePerformancePeriod: (periodId: string) => Promise<void>;
  upsertEquipment: (equipment: Equipment) => Promise<void>;
  removeEquipment: (equipmentId: string) => Promise<void>;
  setCompanyLifecycle: (
    companyId: string,
    status: "active" | "archived",
  ) => Promise<void>;
  setSiteLifecycle: (
    siteId: string,
    status: "active" | "archived",
  ) => Promise<void>;
  setDeliveryPointStatus: (
    pointId: string,
    status: DeliveryPoint["status"],
  ) => Promise<void>;
  setWorkflowStatus: (
    kind: WorkflowKind,
    id: string,
    status: WorkflowItem["status"],
  ) => Promise<WorkflowItem>;
  upsertWorkflowItem: (
    kind: WorkflowKind,
    item: WorkflowItem,
  ) => Promise<WorkflowItem>;
  correctMeasurement: (
    seriesId: string,
    valueId: string,
    value: number | null,
  ) => Promise<void>;
  convertRecommendationToWork: (
    recommendationId: string,
  ) => Promise<WorkflowItem>;
  calculateAnalysis: (request: AnalysisRequest) => Promise<AnalysisResponse>;
  getAnalysisAvailability: (siteId: string) => Promise<AnalysisAvailability[]>;
  /** Station météo retenue par paramètre pour un Site, sur les douze derniers mois. */
  getSiteOfficialDegreeDays: (
    siteId: string,
    from: string,
    to: string,
  ) => Promise<SiteOfficialDegreeDays>;
  getSiteWeather: (siteId: string) => Promise<SiteWeatherCoverage[]>;
}

const EmsContext = createContext<EmsContextValue | null>(null);

export function emptyBrandData(brands: Brand[] = []): BrandData {
  return {
    brands,
    users: [],
    companies: [],
    sites: [],
    deliveryPoints: [],
    siteAreas: [],
    tags: [],
    siteTags: [],
    meters: [],
    equipment: [],
    series: [],
    performancePeriods: [],
    anomalies: [],
    recommendations: [],
    works: [],
    reports: [],
    audit: [],
  };
}

export function effectiveRole(session: Session): Role {
  return session.user.platformRole ?? session.activeMembership.role;
}

/**
 * Rôle affiché : l'aperçu ne vaut que pour un super-admin ; un autre profil
 * garde son rôle réel même si un aperçu traîne dans le stockage de l'onglet.
 */
export function displayedRole(
  session: Session,
  previewRole: PreviewRole | null,
): Role {
  return previewRole && session.user.platformRole === "super_admin"
    ? previewRole
    : effectiveRole(session);
}

/** Charge toutes les collections de la marque active par leurs routes unitaires. */
export async function loadBrandData(
  client: EmsApiClient,
  session: Session,
): Promise<BrandData> {
  const brandId = session.brand.id;
  const isSuper = session.user.platformRole === "super_admin";
  const [
    brands,
    users,
    companies,
    sites,
    deliveryPoints,
    siteAreas,
    tags,
    siteTags,
    meters,
    equipment,
    series,
    performancePeriods,
    anomalies,
    recommendations,
    works,
    reports,
    audit,
  ] = await Promise.all([
    isSuper ? client.listBrands() : Promise.resolve(session.brands),
    client.getBrandCollection(brandId, "users"),
    client.getBrandCollection(brandId, "companies"),
    client.getBrandCollection(brandId, "sites"),
    client.getBrandCollection(brandId, "delivery-points"),
    client.getBrandCollection(brandId, "site-areas"),
    client.getBrandCollection(brandId, "tags"),
    client.listSiteTags(brandId),
    client.getBrandCollection(brandId, "meters"),
    client.getBrandCollection(brandId, "equipment"),
    client.getBrandCollection(brandId, "measurement-series"),
    client.listPerformancePeriods(brandId),
    client.getBrandCollection(brandId, "anomalies"),
    client.getBrandCollection(brandId, "recommendations"),
    client.getBrandCollection(brandId, "works"),
    client.getBrandCollection(brandId, "reports"),
    client.listAuditEvents(brandId, { limit: 500 }),
  ]);
  return {
    brands,
    users,
    companies,
    sites,
    deliveryPoints,
    siteAreas,
    tags,
    siteTags,
    meters,
    equipment,
    series,
    performancePeriods,
    anomalies,
    recommendations,
    works,
    reports,
    audit,
  };
}

export function EmsProvider({ children }: { children: ReactNode }): ReactNode {
  const isPublicMandateRoute = window.location.pathname.startsWith("/mandate/");
  const queryClient = useQueryClient();

  const publicBrands = useQuery({
    queryKey: ["public-brands"],
    queryFn: () => apiClient.listPublicBrands(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const probeSession = !isPublicMandateRoute && hasSignedInMarker();
  const sessionQuery = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async (): Promise<Session | null> => {
      try {
        return await apiClient.getSession();
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    enabled: probeSession,
    retry: false,
    staleTime: 60_000,
  });
  const session = sessionQuery.data ?? null;
  const brandId = session?.brand.id ?? "";
  const [storedPreview, setStoredPreview] = useState<PreviewRole | null>(
    storedPreviewRole,
  );
  const previewRole =
    session && session.user.platformRole === "super_admin"
      ? storedPreview
      : null;
  const setPreviewRole = useCallback((next: PreviewRole | null): void => {
    storePreviewRole(next);
    setStoredPreview(next);
  }, []);

  const brandData = useQuery({
    queryKey: [BRAND_DATA_QUERY_KEY, brandId, session?.user.id ?? ""],
    queryFn: () => {
      if (!session) throw new Error("Session requise.");
      return loadBrandData(apiClient, session);
    },
    enabled: Boolean(session) && !isPublicMandateRoute,
    retry: false,
  });

  const db = useMemo<BrandData>(() => {
    if (brandData.data) return brandData.data;
    const brands = session?.brands ?? publicBrands.data ?? [];
    return emptyBrandData(brands);
  }, [brandData.data, publicBrands.data, session?.brands]);

  const refreshBrandData = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [BRAND_DATA_QUERY_KEY] });
    await queryClient.refetchQueries({
      queryKey: [BRAND_DATA_QUERY_KEY],
      type: "active",
    });
  }, [queryClient]);

  // Synchronisations ciblées en cours : interrogées toutes les deux secondes
  // tant qu'il en reste une, puis plus du tout. Quand un point quitte la
  // liste, ses nouvelles séries sont chargées avec les données de la marque.
  const pointSyncs = useQuery({
    queryKey: [POINT_SYNCS_QUERY_KEY, brandId],
    queryFn: () => apiClient.listPointSyncs(brandId),
    enabled: Boolean(session) && !isPublicMandateRoute,
    retry: false,
    refetchInterval: (query) => (query.state.data?.length ? 2_000 : false),
  });
  const syncingPointIds = useMemo(
    () => (pointSyncs.data ?? []).map((item) => item.pointId),
    [pointSyncs.data],
  );
  const previouslySyncing = useRef<string[]>([]);
  useEffect(() => {
    const finished = previouslySyncing.current.filter(
      (pointId) => !syncingPointIds.includes(pointId),
    );
    previouslySyncing.current = syncingPointIds;
    if (finished.length > 0) void refreshBrandData();
  }, [refreshBrandData, syncingPointIds]);

  const refreshSession = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    await queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY });
  }, [queryClient]);

  const applySession = useCallback(
    (next: Session | null): void => {
      queryClient.setQueryData(SESSION_QUERY_KEY, next);
      if (!next) {
        queryClient.removeQueries({ queryKey: [BRAND_DATA_QUERY_KEY] });
        queryClient.removeQueries({ queryKey: [MEASUREMENTS_QUERY_KEY] });
        setPreviewRole(null);
      }
    },
    [queryClient, setPreviewRole],
  );

  const requireSession = useCallback((): Session => {
    if (!session) throw new Error("Session requise.");
    return session;
  }, [session]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const result = await apiClient.login(email, password);
      if (result.status === "totp_required") {
        return { requiresTotp: true, challengeId: result.challengeId ?? "" };
      }
      if (!result.session) throw new Error("Session absente de la réponse.");
      applySession(result.session);
      return { requiresTotp: false, role: effectiveRole(result.session) };
    },
    [applySession],
  );

  const confirmTotp = useCallback(
    async (challengeId: string, code: string): Promise<Role> => {
      const next = await apiClient.confirmTotp(challengeId, code);
      applySession(next);
      return effectiveRole(next);
    },
    [applySession],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiClient.logout();
    } catch {
      // Une session déjà expirée côté serveur est un état final acceptable.
    }
    applySession(null);
  }, [applySession]);

  const switchBrand = useCallback(
    async (nextBrandId: string): Promise<void> => {
      requireSession();
      await apiClient.switchBrand(nextBrandId);
      await refreshSession();
    },
    [refreshSession, requireSession],
  );

  const resetDemo = useCallback(async (): Promise<void> => {
    await apiClient.resetDemo();
    applySession(null);
  }, [applySession]);

  const upsertUser = useCallback(
    async (user: User, initialGrant?: AccessGrant): Promise<void> => {
      const current = requireSession();
      const exists = db.users.some((item) => item.id === user.id);
      if (exists) {
        await apiClient.updateUser(current.brand.id, user.id, {
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone,
          ...(user.status === "invited" ? {} : { status: user.status }),
        });
      } else {
        if (!initialGrant) {
          throw new Error(
            "Sélectionnez un périmètre avant d’envoyer l’invitation.",
          );
        }
        await apiClient.inviteAccessUser(current.brand.id, user, initialGrant);
      }
      await refreshBrandData();
    },
    [db.users, refreshBrandData, requireSession],
  );

  const setUserStatus = useCallback(
    async (
      userId: string,
      status: "active" | "suspended" | "disabled",
    ): Promise<void> => {
      const current = requireSession();
      await apiClient.updateUser(current.brand.id, userId, { status });
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const removeUser = useCallback(
    async (userId: string, confirmationEmail: string): Promise<void> => {
      const current = requireSession();
      await apiClient.deleteUser(current.brand.id, userId, {
        confirmationEmail,
      });
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const updateUserGrants = useCallback(
    async (
      userId: string,
      membershipId: string,
      grants: AccessGrant[],
    ): Promise<void> => {
      const current = requireSession();
      const target = db.users.find((item) => item.id === userId);
      const membership = target?.memberships.find(
        (item) => item.id === membershipId,
      );
      if (!target || !membership || membership.brandId !== current.brand.id) {
        throw new Error("Le compte ou le périmètre est introuvable.");
      }
      const currentGrants = membership.grants;
      const additions = grants.filter(
        (grant) => !currentGrants.some((item) => item.id === grant.id),
      );
      const removals = currentGrants.filter(
        (grant) => !grants.some((item) => item.id === grant.id),
      );
      if (
        current.activeMembership.role === "manager" &&
        !current.user.platformRole
      ) {
        for (const grant of additions) {
          const source = current.activeMembership.grants.find((own) =>
            grantIsWithin(own, grant, db, current.brand.id),
          );
          if (!source) {
            throw new Error(
              "Le périmètre demandé dépasse celui de l’Energy Manager.",
            );
          }
        }
      }
      // Ajouter le sous-périmètre avant la révocation évite toute coupure d'accès.
      for (const grant of additions) {
        await apiClient.assignAccessGrant(current.brand.id, {
          userId,
          membershipId,
          grant,
        });
      }
      for (const grant of removals) {
        await apiClient.revokeAccessGrant(current.brand.id, {
          userId,
          membershipId,
          grantId: grant.id,
        });
      }
      await refreshBrandData();
    },
    [db, refreshBrandData, requireSession],
  );

  const upsertBrand = useCallback(
    async (brand: Brand, legalEntity?: LegalEntity): Promise<void> => {
      const exists = db.brands.some((item) => item.id === brand.id);
      if (exists) await apiClient.updateBrand(brand, legalEntity);
      else await apiClient.createBrand(brand, legalEntity);
      await Promise.all([refreshBrandData(), refreshSession()]);
    },
    [db.brands, refreshBrandData, refreshSession],
  );

  const upsertCompany = useCallback(
    async (company: Company): Promise<void> => {
      const current = requireSession();
      const exists = db.companies.some((item) => item.id === company.id);
      if (exists) await apiClient.updateCompany(current.brand.id, company);
      else
        await apiClient.createReferenceEntity(
          current.brand.id,
          "companies",
          company,
        );
      await refreshBrandData();
    },
    [db.companies, refreshBrandData, requireSession],
  );

  const upsertSite = useCallback(
    async (site: Site): Promise<void> => {
      const current = requireSession();
      const exists = db.sites.some((item) => item.id === site.id);
      if (exists) {
        await apiClient.updateSite(current.brand.id, site);
        await refreshBrandData();
      } else {
        await apiClient.createReferenceEntity(current.brand.id, "sites", site);
        await Promise.all([refreshBrandData(), refreshSession()]);
      }
    },
    [db.sites, refreshBrandData, refreshSession, requireSession],
  );

  const upsertDeliveryPoint = useCallback(
    async (point: DeliveryPoint): Promise<boolean> => {
      const current = requireSession();
      const previous = db.deliveryPoints.find((item) => item.id === point.id);
      if (previous)
        await apiClient.updateDeliveryPoint(current.brand.id, point);
      else
        await apiClient.createReferenceEntity(
          current.brand.id,
          "delivery-points",
          point,
        );
      if (previous) await refreshBrandData();
      else await Promise.all([refreshBrandData(), refreshSession()]);
      const collected = point.type === "PRM" || point.type === "PCE";
      const normalize = (value: string): string =>
        value.replace(/\s/g, "").toUpperCase();
      const syncing =
        collected &&
        point.status === "active" &&
        (!previous ||
          previous.type !== point.type ||
          normalize(previous.reference) !== normalize(point.reference));
      if (syncing) {
        // L'API a marqué le point avant de répondre : la liste rechargée le
        // contient tant que ses relevés s'écrivent, et relance l'interrogation.
        await queryClient.refetchQueries({
          queryKey: [POINT_SYNCS_QUERY_KEY, current.brand.id],
        });
      }
      return syncing;
    },
    [
      db.deliveryPoints,
      queryClient,
      refreshBrandData,
      refreshSession,
      requireSession,
    ],
  );

  const upsertSiteArea = useCallback(
    async (area: SiteArea): Promise<void> => {
      const current = requireSession();
      await apiClient.upsertSiteArea(current.brand.id, area);
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const removeSiteArea = useCallback(
    async (areaId: string): Promise<void> => {
      const current = requireSession();
      await apiClient.deleteSiteArea(current.brand.id, areaId);
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const upsertTag = useCallback(
    async (tag: TagDefinition): Promise<void> => {
      const current = requireSession();
      await apiClient.upsertTag(current.brand.id, tag);
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const toggleSiteTag = useCallback(
    async (assignment: SiteTagAssignment): Promise<void> => {
      const current = requireSession();
      const exists = db.siteTags.some(
        (item) =>
          item.siteId === assignment.siteId && item.tagId === assignment.tagId,
      );
      if (exists) {
        await apiClient.removeSiteTag(
          current.brand.id,
          assignment.siteId,
          assignment.tagId,
        );
      } else {
        await apiClient.assignSiteTag(
          current.brand.id,
          assignment.siteId,
          assignment.tagId,
        );
      }
      await refreshBrandData();
    },
    [db.siteTags, refreshBrandData, requireSession],
  );

  const upsertMeter = useCallback(
    async (meter: Meter): Promise<void> => {
      const current = requireSession();
      await apiClient.upsertMeter(current.brand.id, meter);
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const upsertPerformancePeriod = useCallback(
    async (
      periodId: string,
      input: CpePerformancePeriodInput,
    ): Promise<CpePerformancePeriod> => {
      const current = requireSession();
      const saved = await apiClient.upsertPerformancePeriod(
        current.brand.id,
        periodId,
        input,
      );
      await refreshBrandData();
      return saved;
    },
    [refreshBrandData, requireSession],
  );

  const removePerformancePeriod = useCallback(
    async (periodId: string): Promise<void> => {
      const current = requireSession();
      await apiClient.deletePerformancePeriod(current.brand.id, periodId);
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const removeMeter = useCallback(
    async (meterId: string): Promise<void> => {
      const current = requireSession();
      await apiClient.deleteMeter(current.brand.id, meterId);
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const upsertEquipment = useCallback(
    async (equipment: Equipment): Promise<void> => {
      const current = requireSession();
      await apiClient.upsertEquipment(current.brand.id, equipment);
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const removeEquipment = useCallback(
    async (equipmentId: string): Promise<void> => {
      const current = requireSession();
      await apiClient.deleteEquipment(current.brand.id, equipmentId);
      await refreshBrandData();
    },
    [refreshBrandData, requireSession],
  );

  const setCompanyLifecycle = useCallback(
    async (companyId: string, status: "active" | "archived"): Promise<void> => {
      const company = db.companies.find((item) => item.id === companyId);
      if (!company) throw new Error("Entreprise introuvable.");
      await upsertCompany({ ...company, lifecycleStatus: status });
    },
    [db.companies, upsertCompany],
  );

  const setSiteLifecycle = useCallback(
    async (siteId: string, status: "active" | "archived"): Promise<void> => {
      const site = db.sites.find((item) => item.id === siteId);
      if (!site) throw new Error("Site introuvable.");
      await upsertSite({ ...site, lifecycleStatus: status });
    },
    [db.sites, upsertSite],
  );

  const setDeliveryPointStatus = useCallback(
    async (pointId: string, status: DeliveryPoint["status"]): Promise<void> => {
      const point = db.deliveryPoints.find((item) => item.id === pointId);
      if (!point) throw new Error("Point introuvable.");
      await upsertDeliveryPoint({ ...point, status });
    },
    [db.deliveryPoints, upsertDeliveryPoint],
  );

  const upsertWorkflowItem = useCallback(
    async (kind: WorkflowKind, item: WorkflowItem): Promise<WorkflowItem> => {
      const current = requireSession();
      const persisted = await apiClient.upsertWorkflowItem(
        current.brand.id,
        kind,
        item,
      );
      await refreshBrandData();
      return persisted;
    },
    [refreshBrandData, requireSession],
  );

  const setWorkflowStatus = useCallback(
    async (
      kind: WorkflowKind,
      id: string,
      status: WorkflowItem["status"],
    ): Promise<WorkflowItem> => {
      const current = requireSession();
      const actor = `${current.user.firstName} ${current.user.lastName}`;
      const item = db[kind].find((candidate) => candidate.id === id);
      if (!item) throw new Error("Élément métier introuvable.");
      return upsertWorkflowItem(kind, {
        ...item,
        status,
        history: [
          ...(item.history ?? []),
          {
            id: crypto.randomUUID(),
            at: new Date().toISOString(),
            actor,
            action:
              status === "published" ? "Publication" : "Retrait de publication",
          },
        ],
      });
    },
    [db, requireSession, upsertWorkflowItem],
  );

  const convertRecommendationToWork = useCallback(
    async (recommendationId: string): Promise<WorkflowItem> => {
      const current = requireSession();
      const recommendation = db.recommendations.find(
        (item) => item.id === recommendationId,
      );
      if (!recommendation) throw new Error("Recommandation introuvable.");
      const actor = `${current.user.firstName} ${current.user.lastName}`;
      const at = new Date().toISOString();
      const accepted: WorkflowItem = {
        ...recommendation,
        stage: "accepted",
        history: [
          ...(recommendation.history ?? []),
          {
            id: crypto.randomUUID(),
            at,
            actor,
            action: "Recommandation acceptee et transformee en travaux",
          },
        ],
      };
      const existingWork = db.works.find((item) =>
        item.sourceIds?.includes(recommendationId),
      );
      const persisted = await apiClient.upsertWorkflowItem(
        current.brand.id,
        "recommendations",
        accepted,
      );
      if (!existingWork) {
        await apiClient.upsertWorkflowItem(current.brand.id, "works", {
          ...recommendation,
          id: crypto.randomUUID(),
          title: `Travaux · ${recommendation.title}`,
          status: "draft",
          stage: "planned",
          createdAt: at,
          sourceIds: [recommendationId, ...(recommendation.sourceIds ?? [])],
          history: [
            {
              id: crypto.randomUUID(),
              at,
              actor,
              action: "Creation depuis une recommandation acceptee",
              detail: recommendationId,
            },
          ],
        });
      }
      await refreshBrandData();
      return persisted;
    },
    [db.recommendations, db.works, refreshBrandData, requireSession],
  );

  const correctMeasurement = useCallback(
    async (seriesId: string, valueId: string, value: number | null) => {
      const current = requireSession();
      await apiClient.correctMeasurement(current.brand.id, seriesId, valueId, {
        value,
        quality: value === null ? "missing" : "corrected",
      });
      await queryClient.invalidateQueries({
        queryKey: [MEASUREMENTS_QUERY_KEY, current.brand.id, seriesId],
      });
      await refreshBrandData();
    },
    [queryClient, refreshBrandData, requireSession],
  );

  const calculateAnalysis = useCallback(
    async (request: AnalysisRequest): Promise<AnalysisResponse> => {
      const current = requireSession();
      return apiClient.calculateAnalysis(current.brand.id, request);
    },
    [requireSession],
  );

  const getAnalysisAvailability = useCallback(
    async (siteId: string): Promise<AnalysisAvailability[]> => {
      const current = requireSession();
      return apiClient.getAnalysisAvailability(current.brand.id, siteId);
    },
    [requireSession],
  );

  const getSiteOfficialDegreeDays = useCallback(
    async (
      siteId: string,
      from: string,
      to: string,
    ): Promise<SiteOfficialDegreeDays> => {
      const current = requireSession();
      return apiClient.getSiteOfficialDegreeDays(
        current.brand.id,
        siteId,
        from,
        to,
      );
    },
    [requireSession],
  );

  const getSiteWeather = useCallback(
    async (siteId: string): Promise<SiteWeatherCoverage[]> => {
      const current = requireSession();
      return apiClient.getSiteWeather(current.brand.id, siteId);
    },
    [requireSession],
  );

  const retryApi = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: ["public-brands"] });
    await refreshSession();
    await refreshBrandData();
  }, [queryClient, refreshBrandData, refreshSession]);

  const sessionSettled = !probeSession || !sessionQuery.isPending;
  const dataSettled = !session || brandData.isSuccess || brandData.isError;
  const ready =
    sessionSettled &&
    dataSettled &&
    (!publicBrands.isPending || db.brands.length > 0);
  const failure =
    sessionQuery.error ??
    brandData.error ??
    (db.brands.length ? null : publicBrands.error);
  const apiState: EmsContextValue["apiState"] = !ready
    ? "loading"
    : failure
      ? failure instanceof ApiError && failure.status === 403
        ? "forbidden"
        : "error"
      : "ready";

  const value = useMemo<EmsContextValue>(
    () => ({
      ready,
      apiState,
      apiError: failure instanceof Error ? failure.message : "",
      retryApi,
      db,
      session,
      role: session ? displayedRole(session, previewRole) : null,
      previewRole,
      setPreviewRole,
      login,
      confirmTotp,
      logout,
      switchBrand,
      refreshSession,
      refreshBrandData,
      resetDemo,
      upsertUser,
      setUserStatus,
      removeUser,
      updateUserGrants,
      upsertBrand,
      upsertCompany,
      upsertSite,
      upsertDeliveryPoint,
      syncingPointIds,
      upsertSiteArea,
      removeSiteArea,
      upsertTag,
      toggleSiteTag,
      upsertMeter,
      removeMeter,
      upsertPerformancePeriod,
      removePerformancePeriod,
      upsertEquipment,
      removeEquipment,
      setCompanyLifecycle,
      setSiteLifecycle,
      setDeliveryPointStatus,
      setWorkflowStatus,
      upsertWorkflowItem,
      correctMeasurement,
      convertRecommendationToWork,
      calculateAnalysis,
      getAnalysisAvailability,
      getSiteWeather,
      getSiteOfficialDegreeDays,
    }),
    [
      apiState,
      calculateAnalysis,
      confirmTotp,
      convertRecommendationToWork,
      correctMeasurement,
      db,
      failure,
      getAnalysisAvailability,
      getSiteWeather,
      getSiteOfficialDegreeDays,
      login,
      logout,
      previewRole,
      ready,
      refreshBrandData,
      refreshSession,
      removeUser,
      removeEquipment,
      removeMeter,
      removePerformancePeriod,
      removeSiteArea,
      resetDemo,
      retryApi,
      session,
      setCompanyLifecycle,
      setDeliveryPointStatus,
      setPreviewRole,
      setSiteLifecycle,
      setWorkflowStatus,
      switchBrand,
      syncingPointIds,
      toggleSiteTag,
      updateUserGrants,
      upsertBrand,
      upsertCompany,
      upsertDeliveryPoint,
      upsertEquipment,
      upsertMeter,
      upsertPerformancePeriod,
      upsertSite,
      upsertSiteArea,
      upsertTag,
      upsertUser,
      setUserStatus,
      upsertWorkflowItem,
    ],
  );

  return <EmsContext value={value}>{children}</EmsContext>;
}

export function useEms(): EmsContextValue {
  const context = use(EmsContext);
  if (!context) throw new Error("useEms must be used inside EmsProvider.");
  return context;
}
